import React from 'react'
import { useGetBusinessID } from '@marketing-milk/frontend'
import { Button, Menu, MenuItem } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { PageBarFilters, PageBarSearch } from 'components/layout/PageBar'
import { fuzzySearch } from 'components/DataTable/helpers/search.helpers'
import { SelectCreativeType } from 'components/FormFields/Select/SelectCreativeType'
import { useSprint } from 'hooks/api/useSprint'
import { set, sortBy } from 'lodash/fp'
import { NewCreativeModal } from 'pages/business/creatives/components/NewCreativeModal/NewCreativeModal'
import { CreativeCard } from 'pages/business/creatives/CreativeCard/CreativeCard'
import { CreativeCardSkeleton } from 'pages/business/creatives/CreativeCard/CreativeCardSkeleton'
import { useCreatives } from 'pages/business/creatives/useCreatives'
import { useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  creativeFilterPredicate,
  CreativeFilters,
} from '../../../creatives/helpers/creative.helpers'
import { BulkImportCreatives } from '../bulk-import-creatives/BulkImportCreatives'

interface SprintsCreativesTabProps {
  sprintId: number
  navigation: React.ReactNode
}

export const SprintsCreativesTab = (props: SprintsCreativesTabProps) => {
  const businessID = useGetBusinessID()
  const history = useHistory()

  const { isRefetchingCreatives } = useCreatives(businessID)
  const { sprint, sprintCreatives, sprintCreativesLoading } = useSprint(props.sprintId)

  const [searchTerm, setSearchTerm] = useState('')
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const [creativeAction, setCreativeAction] = useState<'create' | 'copy' | null>(null)
  const addButtonRef = useRef<HTMLButtonElement>(null)
  const [filters, setFilters] = useState<CreativeFilters>({})

  const showLoader = useMemo(
    () => sprintCreativesLoading || isRefetchingCreatives,
    [sprintCreativesLoading, isRefetchingCreatives]
  )

  const isFirstLoad = sprintCreativesLoading && !sprintCreatives?.length

  const visibleCreatives = sortBy('name', fuzzySearch(sprintCreatives || [], searchTerm)).filter(
    creativeFilterPredicate(filters)
  )
  const resetView = () => {
    setSearchTerm('')
    setFilters({})
  }

  if (!sprint) return null
  return (
    <>
      <div className="flex flex-shrink-0 flex-wrap items-center gap-x-4 border-b border-mm-neutral-1-200 bg-white px-4">
        {props.navigation}
        <div
          className="ml-auto flex min-w-0 flex-1 flex-wrap items-center justify-end gap-3 py-1.5"
          style={{ minWidth: 'min(100%, 440px)' }}
        >
          <PageBarSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search creatives"
          />
          <PageBarFilters activeCount={filters.type?.length ?? 0} onClear={() => setFilters({})}>
            <p className="text-xs font-semibold text-mm-neutral-1-600">Creative type</p>
            <SelectCreativeType
              value={filters.type ?? []}
              placeholder="Any type"
              onSelectType={types => setFilters(set('type', types))}
            />
          </PageBarFilters>
          <Button
            ref={addButtonRef}
            variant="text"
            color="primary"
            endIcon={<KeyboardArrowDownIcon />}
            aria-haspopup="menu"
            aria-controls={addMenuOpen ? 'sprint-add-creative-menu' : undefined}
            aria-expanded={addMenuOpen}
            onClick={() => setAddMenuOpen(true)}
          >
            Add creative
          </Button>
          <Menu
            id="sprint-add-creative-menu"
            anchorEl={addButtonRef.current}
            open={addMenuOpen}
            onClose={() => setAddMenuOpen(false)}
          >
            <MenuItem
              onClick={() => {
                setAddMenuOpen(false)
                setCreativeAction('create')
              }}
            >
              Create new creative
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAddMenuOpen(false)
                setCreativeAction('copy')
              }}
            >
              Copy existing creatives
            </MenuItem>
          </Menu>
          {creativeAction === 'create' && (
            <NewCreativeModal
              sprintId={sprint.id}
              open
              anchorEl={addButtonRef.current}
              onClose={() => setCreativeAction(null)}
            />
          )}
          {creativeAction === 'copy' && (
            <BulkImportCreatives
              sprintId={sprint.id}
              open
              onClose={() => setCreativeAction(null)}
            />
          )}
          <p className="whitespace-nowrap text-xs text-mm-neutral-1-600" role="status">
            {isFirstLoad
              ? 'Loading creatives…'
              : isRefetchingCreatives
              ? 'Refreshing creatives…'
              : `Showing ${visibleCreatives.length} of ${sprintCreatives?.length ?? 0} creatives`}
          </p>
        </div>
      </div>

      <div
        role="tabpanel"
        id="sprint-panel-creatives"
        aria-labelledby="sprint-tab-creatives"
        tabIndex={0}
        className="min-h-0 min-w-0 flex-1 overflow-y-auto bg-mm-neutral-2-50"
      >
        {!showLoader && sprintCreatives?.length === 0 && (
          <div className="p-4 text-center">
            <h3 className="mt-6 font-semibold text-mm-neutral-1-900">
              No creatives in this sprint yet
            </h3>
            <p className="mt-2 text-sm text-mm-neutral-1-600">
              Use Add creative to create a new creative or copy existing creatives into this sprint.
            </p>
          </div>
        )}

        {!showLoader && !!sprintCreatives?.length && visibleCreatives.length === 0 && (
          <div className="p-8 text-center">
            <h3 className="font-semibold text-mm-neutral-1-900">No matching creatives</h3>
            <p className="mb-3 mt-2 text-sm text-mm-neutral-1-600">
              Try another search or clear your filters to see all creatives in this sprint.
            </p>
            <Button variant="outlined" onClick={resetView}>
              Clear search and filters
            </Button>
          </div>
        )}

        <div
          aria-busy={isFirstLoad}
          className="grid w-full grid-cols-[repeat(auto-fill,minmax(min(100%,20rem),1fr))] gap-5 p-4"
        >
          {isFirstLoad
            ? Array.from({ length: 12 }, (_, i) => <CreativeCardSkeleton key={i} />)
            : visibleCreatives.map(creative => (
                <CreativeCard
                  key={creative.id}
                  creative={creative}
                  onSelect={() => history.push(`/${businessID}/sprints/${creative.id}`)}
                  pillDisplayed={'reviewStatus'}
                  propertiesDisplayed={['name']}
                />
              ))}
        </div>
      </div>
    </>
  )
}
